import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import type { MustDoActivityItem } from 'tripPlanner/hooks/api/mustDoActivities'
import type { ThemeItem } from 'tripPlanner/hooks/api/tripGenerationData'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import type { ActivityBudgetOption, AITripPreferenceStep, DatePickerOption, DietaryRequirementOption, FoodStyleOption, Locations, MustDoOption, OccupancyOption, ProximityOption, TripPaceOption } from 'tripPlanner/pages/trip/types'
import AITripBudgetSelection from './AITripActivityBudgetSelection'
import AITripFoodStyleSelection from './AITripFoodStyleSelection'
import AITripFreeTextSelection from './AITripFreeTextSelection'
import AITripMustDoSelection from './AITripMustDoSelection'
import AITripPaceSelection from './AITripPaceSelection'
import AITripProximitySelection from './AITripProximitySelection'
import type { TripGenerationFlow } from './AITripStartSelection'
import AITripStartSelection from './AITripStartSelection'
import AITripThemeSelection from './AITripThemeSelection'
import AITripOccupancySelection from './AITripOccupancySelection'
import AITripDatesSelection from './AITripDatesSelection'
import AITripDestinationSelectionV2 from './AITripDestinationSelectionV2'

const PrimaryContainer = styled(Group)`
  padding: 0 ${rem(20)};
  height: fit-content;
  max-width: ${rem(640)};

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(60)};
    max-width: ${rem(720)};
  }
`

interface Props {
  hotelCity: string
  onGenerateTrip: (
    type: 'customised' | 'non-customised',
    themes: Array<{
      theme: string
      selected: boolean
    }>,
    generateActivities: boolean,
    tripPaces: Array<TripPaceOption>,
    activityBudget: Array<ActivityBudgetOption>,
    foodStyle: Array<FoodStyleOption>,
    mustDoActivities: Array<MustDoOption>,
    dietaryRequirements: Array<DietaryRequirementOption>,
    proximity: Array<ProximityOption>,
    freeText: string,
    location: string,
    occupancy: OccupancyOption,
    datePicker: DatePickerOption,
    locations: Array<Locations>,
  ) => void
  isGeneratingTrip: boolean
  step: AITripPreferenceStep
  stepNumbers: Record<AITripPreferenceStep, number>
  totalSteps: number
  mustDoActivities: Array<MustDoActivityItem> | undefined
  handleNext: () => void
  selectedThemeNames: Array<string>
  onThemeToggle: (theme: string) => void
  selectedMustDoActivityNames: Array<string>
  onMustDoActivityToggle: (...values: Array<string>) => void
  tripPaces: Array<TripPaceOption>
  onPaceSelect: (pace: string) => void
  proximity: Array<ProximityOption>
  onProximitySelect: (proximity: string) => void
  foodStyle: Array<FoodStyleOption>
  onFoodStyleSelect: (foodStyle: string) => void
  activityBudget: Array<ActivityBudgetOption>
  onBudgetSelect: (budget: string) => void
  dietaryRequirements: Array<DietaryRequirementOption>
  onDietaryRequirementSelect: (dietaryRequirement: string) => void
  freeText: string
  onFreeTextChange: (value: string) => void
  themes: Array<ThemeItem> | undefined
  themesLoading: boolean
  location: string
  onLocationSelect: (location: string) => void
  locations: Array<Locations>
  onAddLocation?: (params: {
    locationName: string;
    startDate?: Date;
    endDate?: Date;
    occupancy?: OccupancyOption;
    duration?: number;
    destinationId?: string
  }) => void
  onRemoveLocation?: (locationName: string) => void
  onLocationsReorder?: (locations: Array<Locations>) => void
  onLocationDurationChange?: (locationIndex: number, newDuration: number) => void
  occupancy: OccupancyOption
  onOccupancySelect: (occupancy: OccupancyOption) => void
  onUpdateAllLocationsOccupancy?: (occupancy: OccupancyOption) => void
  datePicker: DatePickerOption
  onDatePickerSelect: (datePicker: DatePickerOption) => void
  hasUrlParams?: boolean
  urlLocation?: string
  isSurpriseMe?: boolean
  onGenerateTripFlowSelect: (flow: TripGenerationFlow) => void
  mustDoActivitiesByLocation?: Map<string, Array<MustDoActivityItem>>
}

function AITripPreferences(props: Props) {
  const {
    hotelCity,
    onGenerateTrip,
    isGeneratingTrip,
    themesLoading,
    step,
    stepNumbers,
    totalSteps,
    mustDoActivities,
    themes,
    handleNext,
    selectedThemeNames,
    onThemeToggle,
    selectedMustDoActivityNames,
    onMustDoActivityToggle,
    tripPaces,
    onPaceSelect,
    proximity,
    onProximitySelect,
    foodStyle,
    onFoodStyleSelect,
    activityBudget,
    onBudgetSelect,
    dietaryRequirements,
    onDietaryRequirementSelect,
    freeText,
    onFreeTextChange,
    location,
    onLocationSelect,
    occupancy,
    onOccupancySelect,
    onUpdateAllLocationsOccupancy,
    datePicker,
    onDatePickerSelect,
    hasUrlParams,
    urlLocation,
    onGenerateTripFlowSelect,
    locations,
    onAddLocation,
    onRemoveLocation,
    onLocationsReorder,
    onLocationDurationChange,
    mustDoActivitiesByLocation,
  } = props

  const handleGenerateTripWithAuth = useCallback(() => {
    const selectedThemes = themes?.map(theme => ({
      theme: theme.theme,
      selected: selectedThemeNames.includes(theme.theme),
    })) ?? []

    let selectedMustDoActivities: Array<MustDoOption> = []
    if (mustDoActivitiesByLocation && mustDoActivitiesByLocation.size > 0) {
      const allActivitiesMap = new Map<string, MustDoActivityItem>()
      mustDoActivitiesByLocation.forEach((activities) => {
        activities.forEach(activity => {
          allActivitiesMap.set(activity.activity, activity)
        })
      })

      selectedMustDoActivities = Array.from(allActivitiesMap.values()).map(activity => ({
        activity: activity.activity,
        selected: selectedMustDoActivityNames.includes(activity.activity),
      }))
    } else {
      selectedMustDoActivities = mustDoActivities?.map(activity => ({
        activity: activity.activity,
        selected: selectedMustDoActivityNames.includes(activity.activity),
      })) ?? []
    }

    onGenerateTrip(
      'customised',
      selectedThemes,
      true,
      tripPaces,
      activityBudget,
      foodStyle,
      selectedMustDoActivities,
      dietaryRequirements,
      proximity,
      freeText,
      location,
      occupancy,
      datePicker,
      locations,
    )
  }, [
    themes,
    selectedThemeNames,
    selectedMustDoActivityNames,
    onGenerateTrip,
    tripPaces,
    activityBudget,
    foodStyle,
    mustDoActivities,
    dietaryRequirements,
    proximity,
    freeText,
    location,
    occupancy,
    datePicker,
    locations,
    mustDoActivitiesByLocation,
  ])

  const handleSurpriseMe = useCallback(() => {
    if (!hasUrlParams || (hasUrlParams && urlLocation)) {
      handleNext()
    } else {
      onGenerateTrip('non-customised', [], true, tripPaces, activityBudget, foodStyle, [], dietaryRequirements, proximity, '', location, occupancy, datePicker, locations)
    }
  }, [hasUrlParams, urlLocation, handleNext, onGenerateTrip, tripPaces, activityBudget, foodStyle, dietaryRequirements, proximity, location, occupancy, datePicker, locations])

  const handleLocationDelete = useCallback((locationIndex: number) => {
    if (onRemoveLocation && locations[locationIndex]) {
      onRemoveLocation(locations[locationIndex].locationName)
    }
  }, [onRemoveLocation, locations])

  const handleGenerateTrip = usePendingLoginHandler(
    handleGenerateTripWithAuth,
    'tripPlannerPagesLogin',
  )

  const handleSurpriseMeGenerateTrip = usePendingLoginHandler(
    handleSurpriseMe,
    'tripPlannerPagesLogin',
  )

  return (
    <PrimaryContainer direction="vertical" gap={20} tabletGap={40} fullWidth>
      {step === 'start' && (
        <AITripStartSelection
          hotelCity={hotelCity}
          handleCustomise={handleNext}
          handleSurpriseMe={handleSurpriseMeGenerateTrip}
          isGeneratingTrip={isGeneratingTrip}
          onGenerateTripFlowSelect={onGenerateTripFlowSelect}
        />
      )}
      {step === 'location' && (
        <AITripDestinationSelectionV2
          location={location}
          onLocationSelect={onLocationSelect}
          stepNumber={stepNumbers.location}
          totalSteps={totalSteps}
          onAddLocation={onAddLocation}
          locations={locations}
          onRemoveLocation={onRemoveLocation}
        />
      )}
      {step === 'occupancy' && (
        <AITripOccupancySelection
          occupancy={occupancy}
          onOccupancySelect={onOccupancySelect}
          stepNumber={stepNumbers.occupancy}
          totalSteps={totalSteps}
          onNext={handleNext}
          onUpdateAllLocationsOccupancy={onUpdateAllLocationsOccupancy}
        />
      )}
      {step === 'datePicker' && (
        <AITripDatesSelection
          datePicker={datePicker}
          onDatePickerSelect={onDatePickerSelect}
          stepNumber={stepNumbers.datePicker}
          totalSteps={totalSteps}
          locations={locations}
          onLocationsReorder={onLocationsReorder}
          onLocationDurationChange={onLocationDurationChange}
          onLocationDelete={handleLocationDelete}
        />
      )}
      {step === 'pace' && (
        <AITripPaceSelection
          hotelCity={hotelCity}
          tripPaces={tripPaces}
          onPaceSelect={onPaceSelect}
          onNext={handleNext}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.pace}
          totalSteps={totalSteps}
        />
      )}

      {step === 'proximity' && (
        <AITripProximitySelection
          proximity={proximity}
          onProximitySelect={onProximitySelect}
          onNext={handleNext}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.proximity}
          totalSteps={totalSteps}
        />
      )}

      {step === 'budget' && (
        <AITripBudgetSelection
          activityBudget={activityBudget}
          onBudgetSelect={onBudgetSelect}
          onNext={handleNext}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.budget}
          totalSteps={totalSteps}
        />
      )}

      {step === 'theme' && themes && (
        <AITripThemeSelection
          themes={themes}
          themesLoading={themesLoading}
          selectedThemeNames={selectedThemeNames}
          onThemeToggle={onThemeToggle}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.theme}
          totalSteps={totalSteps}
        />
      )}

      {(step === 'mustDo' || step.startsWith('mustDo')) && !!mustDoActivities?.length && (
        <AITripMustDoSelection
          hotelCity={hotelCity}
          showLocationInHeading={locations.length > 1}
          mustDoActivities={mustDoActivities}
          onMustDoActivityToggle={onMustDoActivityToggle}
          selectedMustDoActivityNames={selectedMustDoActivityNames}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers[step]}
          totalSteps={totalSteps}
        />
      )}

      {step === 'foodStyle' && (
        <AITripFoodStyleSelection
          hotelCity={hotelCity}
          foodStyles={foodStyle}
          dietaryRequirements={dietaryRequirements}
          onFoodStyleSelect={onFoodStyleSelect}
          onDietaryRequirementSelect={onDietaryRequirementSelect}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.foodStyle}
          totalSteps={totalSteps}
        />
      )}

      {step === 'freeText' && (
        <AITripFreeTextSelection
          hotelCity={hotelCity}
          freeText={freeText}
          onFreeTextChange={onFreeTextChange}
          onGenerate={handleGenerateTrip}
          isGeneratingTrip={isGeneratingTrip}
          stepNumber={stepNumbers.freeText}
          totalSteps={totalSteps}
        />
      )}

    </PrimaryContainer>
  )
}

export default AITripPreferences
